import 'swiper/css'
import 'swiper/css/autoplay'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/virtual'

import {
    type BackgroundNoImageProtocol,
    type Fit,
    type ImageInfo,
    type ImageSwiperItem,
    ImageVariableValue,
    type Shape
} from '@lighthouse/core'
import { getImageFullUrlInApplication } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { Autoplay, EffectCreative, Navigation, Pagination, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import FileItem from './FileItem'
import * as SC from './styles'

interface CarouselProps {
    blockId: string
    noLazy?: boolean
    autoPlay?: boolean
    swipeSpeed: number
    fitType: Fit
    value: ImageSwiperItem[]
    appId?: string
    background?: BackgroundNoImageProtocol
    aspectRatio: string
    shape: Shape
    radius?: string
    width: number
    compress?: boolean
    isClickEvent?: boolean
    handImageClick: (ev: React.MouseEvent, imageInfo: ImageInfo) => void
    hasCustomCursor: boolean
}

export const Carousel = (props: CarouselProps) => {
    const {
        blockId,
        noLazy = false,
        autoPlay,
        swipeSpeed,
        fitType,
        value,
        appId,
        background,
        aspectRatio,
        shape,
        radius,
        width,
        compress,
        isClickEvent,
        handImageClick,
        hasCustomCursor
    } = props

    const borderRadius = useMemo(() => {
        if (shape === 'round') {
            return '50%'
        }
        return radius ? `${radius}px` : undefined
    }, [shape, radius])
    return (
        <Swiper
            key={`${blockId}-${autoPlay}-${swipeSpeed}`}
            effect="creative"
            creativeEffect={{
                prev: {
                    shadow: true,
                    translate: ['-120%', 0, -200]
                },
                next: {
                    shadow: true,
                    translate: ['120%', 0, -200]
                }
            }}
            modules={[Autoplay, EffectCreative, Pagination, Navigation, Virtual]}
            pagination={{ el: `[data-block-id = ${blockId}] .swiperPagination` }}
            navigation={{
                prevEl: `[data-block-id = ${blockId}] .swiperNavigationLeft`,
                nextEl: `[data-block-id = ${blockId}] .swiperNavigationRight`
            }}
            preventClicks={false}
            speed={600}
            loop
            autoplay={
                autoPlay
                    ? {
                          delay: swipeSpeed.toString() === '' ? undefined : Number(swipeSpeed) * 1000,
                          pauseOnMouseEnter: true
                      }
                    : false
            }
        >
            <SC.Pagination className="swiperPagination" />

            <SC.Navigation>
                <SC.NavigationButton className="swiperNavigationLeft" style={{ left: 23, right: '50%' }}>
                    <SC.Icon type="ArrowLeftSmall" color="#fff" />
                </SC.NavigationButton>
                <SC.NavigationButton className="swiperNavigationRight" style={{ right: 23, left: '50%' }}>
                    <SC.Icon type="ArrowRightSmall" color="#fff" />
                </SC.NavigationButton>
            </SC.Navigation>

            {value.map((item, index) => {
                return (
                    <SwiperSlide key={item.url}>
                        <FileItem
                            {...item}
                            noLazy={noLazy}
                            src={getImageFullUrlInApplication(appId, item.url, {
                                width,
                                compress
                            })}
                            style={{ borderRadius, cursor: isClickEvent || hasCustomCursor ? undefined : 'pointer' }}
                            aspectRatio={aspectRatio}
                            objectFit={fitType}
                            data-stop-action-propagation={isClickEvent ? undefined : true}
                            onClick={ev =>
                                handImageClick(ev, {
                                    [ImageVariableValue.IMAGE_INDEX]: index,
                                    [ImageVariableValue.IMAGE_URL]: item.url,
                                    [ImageVariableValue.IMAGE_NAME]: item.title
                                })
                            }
                            alt={item.title}
                        />
                    </SwiperSlide>
                )
            })}
        </Swiper>
    )
}
